'use client';

import { cn, formatDate, formatTruncatedText } from '@/helpers/common';
import { TJobListElement } from '@/ts/jobs';
import { getIndustryTitle, getOccupationTitle } from '@/helpers/taxonomy';
import React from 'react';
import { ROUTES } from '@/constants/routes/default';
import Link from 'next/link';
import StubKD from './stubs/StubKD';
import { STUB_DEFAULT_TEXTS, STUB_ERROR_TEXTS } from '@/constants/kd/default';
import Tooltip from '../Tooltip';

type JobsListKDProps = {
  jobs: TJobListElement[];
  isError?: boolean;
  withCreateProfile?: boolean;
};

type TJobChip = {
  key: string;
  label: string;
  withTooltip: boolean;
};

const CHIP_LABEL_MAX_LENGTH = 40;

const JobChip = ({ chip }: { chip: TJobChip }) => {
  const content = (
    <span className="min-w-0 max-w-full shrink overflow-hidden rounded-sm bg-kd-athens-gray px-2 py-1 text-xs text-kd-mako">
      <span className="block truncate">
        {chip.withTooltip
          ? formatTruncatedText({ text: chip.label, maxLength: CHIP_LABEL_MAX_LENGTH })
          : chip.label}
      </span>
    </span>
  );

  if (!chip.withTooltip) return content;

  return (
    <Tooltip className="max-w-70 whitespace-normal wrap-break-word rounded-lg" tooltip={chip.label}>
      {content}
    </Tooltip>
  );
};

const JobsListKD = ({ jobs, isError = false }: JobsListKDProps) => {
  if (!jobs.length) {
    return <StubKD texts={isError ? STUB_ERROR_TEXTS : STUB_DEFAULT_TEXTS} />;
  }

  return (
    <ul className="mt-5 flex flex-col bg-white custom-shadow-kd rounded-xl" id="jobs-list">
      {jobs.map((job) => {
        const locationLabel = (() => {
          const city = job.city?.trim();
          const state = job.state?.trim();

          if (city && state) return `${city}, ${state}`;
          if (city || state) return city || state || '';
          if (job.isRemote) return 'Remote';

          return '';
        })();

        const dateLabel = formatDate({
          dateValue: job.createdAt,
          variant: 4,
        });
        const chips = [
          { key: 'occupation', label: getOccupationTitle(job.occ_1), withTooltip: true },
          { key: 'industry', label: getIndustryTitle(job.ind_6_1), withTooltip: true },
          { key: 'salary', label: job.salary?.raw, withTooltip: false },
        ].filter((chip): chip is TJobChip => Boolean(chip.label));

        return (
          <React.Fragment key={job.id}>
            <li className="block p-5 bg-white border-b border-kd-athens-gray last:border-b-0 first:rounded-t-xl last:rounded-b-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {job.company && (
                    <span className="font-semibold capitalize text-kd-valencia text-sm">
                      {job.company}
                    </span>
                  )}
                  {job.company && locationLabel && (
                    <span className="inline-block h-0.5 w-0.5 shrink-0 self-center bg-kd-pale-sky" />
                  )}
                  {locationLabel && (
                    <span className="text-kd-pale-sky text-sm">{locationLabel}</span>
                  )}
                </div>
                <p
                  className={cn(
                    'shrink-0 text-[13px]',
                    dateLabel === 'today' || dateLabel === 'yesterday'
                      ? 'text-kd-valencia capitalize'
                      : 'text-kd-mako',
                  )}
                >
                  {dateLabel}
                </p>
              </div>
              <Link
                href={`${ROUTES.jobs}/${job.id}`}
                className="mt-1 block font-semibold text-kd-woodsmoke no-underline hover:text-kd-valencia default-transition"
              >
                {job.title}
              </Link>

              {chips.length > 0 && (
                <div className="mt-3 flex max-w-full flex-wrap gap-2">
                  <div className="flex min-w-0 max-w-full shrink items-center gap-2">
                    {chips.slice(0, 2).map((chip) => (
                      <JobChip key={chip.key} chip={chip} />
                    ))}
                  </div>
                  {chips[2] && <JobChip chip={chips[2]} />}
                </div>
              )}
            </li>
          </React.Fragment>
        );
      })}
    </ul>
  );
};

export default JobsListKD;
